import React, { useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Button,
  Typography,
  Box,
  Alert,
  Container,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { Language as LanguageIcon, CheckCircle, ArrowBack, Error as ErrorIcon } from '@mui/icons-material';
import { useTranslation, Language } from '../../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const { language, setLanguage, t } = useTranslation();
  
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  const handleLangMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    handleLangMenuClose();
  };

  const handleTouchEnd = (ref: React.RefObject<HTMLInputElement>) => {
    return () => {
      setTimeout(() => {
        if (ref.current) {
          ref.current.focus();
        }
      }, 10);
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 6) {
      setError(t('resetPassword.passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('resetPassword.passwordMismatch'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/users/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user: {
            reset_password_token: token,
            password: password,
            password_confirmation: confirmPassword
          }
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.errors?.join(', ') || data.message || t('resetPassword.errorMessage'));
      }
    } catch (err) {
      setError(t('resetPassword.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const nativeInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '16px 14px',
    fontSize: '16px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    outline: 'none',
    fontFamily: 'Roboto, sans-serif',
    boxSizing: 'border-box',
    WebkitAppearance: 'none',
    marginBottom: '16px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    color: '#666',
    fontFamily: 'Roboto, sans-serif',
  };

  // Invalid token state
  if (!token) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, mb: 3 }}>
          <Box
            component="img"
            src="/apple-touch-icon.png"
            alt={t('appName')}
            sx={{ width: 80, height: 80, objectFit: 'contain', mb: 2 }}
          />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main', textAlign: 'center' }}>
            {t('appName')}
          </Typography>
        </Box>

        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" component="h2" gutterBottom>
              {t('resetPassword.invalidToken')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {t('resetPassword.errorMessage')}
            </Typography>
            <Button
              component={Link}
              to="/forgot-password"
              variant="contained"
            >
              {t('forgotPassword.tryAgain')}
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Success state
  if (success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
          <IconButton onClick={handleLangMenuOpen} color="primary" aria-label={t('language.select')}>
            <LanguageIcon />
          </IconButton>
          <Menu
            anchorEl={langAnchorEl}
            open={Boolean(langAnchorEl)}
            onClose={handleLangMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => handleLanguageChange('en')} selected={language === 'en'}>
              {t('language.english')}
            </MenuItem>
            <MenuItem onClick={() => handleLanguageChange('ta')} selected={language === 'ta'}>
              {t('language.tamil')}
            </MenuItem>
          </Menu>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, mb: 3 }}>
          <Box
            component="img"
            src="/apple-touch-icon.png"
            alt={t('appName')}
            sx={{ width: 80, height: 80, objectFit: 'contain', mb: 2 }}
          />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main', textAlign: 'center' }}>
            {t('appName')}
          </Typography>
        </Box>

        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" component="h2" gutterBottom>
              {t('resetPassword.successTitle')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {t('resetPassword.successMessage')}
            </Typography>
            <Button
              component={Link}
              to="/login"
              variant="contained"
            >
              {t('resetPassword.goToLogin')}
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
        <IconButton onClick={handleLangMenuOpen} color="primary" aria-label={t('language.select')}>
          <LanguageIcon />
        </IconButton>
        <Menu
          anchorEl={langAnchorEl}
          open={Boolean(langAnchorEl)}
          onClose={handleLangMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => handleLanguageChange('en')} selected={language === 'en'}>
            {t('language.english')}
          </MenuItem>
          <MenuItem onClick={() => handleLanguageChange('ta')} selected={language === 'ta'}>
            {t('language.tamil')}
          </MenuItem>
        </Menu>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, mb: 3 }}>
        <Box
          component="img"
          src="/apple-touch-icon.png"
          alt={t('appName')}
          sx={{ width: 80, height: 80, objectFit: 'contain', mb: 2 }}
        />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main', textAlign: 'center' }}>
          {t('appName')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('managementSystem')}
        </Typography>
      </Box>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('resetPassword.title')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t('resetPassword.subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginTop: '16px' }}>
              <label htmlFor="password" style={labelStyle}>
                {t('resetPassword.newPassword')}
              </label>
              <input
                ref={passwordRef}
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onTouchEnd={handleTouchEnd(passwordRef)}
                required
                disabled={loading}
                autoComplete="new-password"
                minLength={6}
                style={{
                  ...nativeInputStyle,
                  backgroundColor: loading ? '#f5f5f5' : 'white',
                }}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" style={labelStyle}>
                {t('resetPassword.confirmPassword')}
              </label>
              <input
                ref={confirmPasswordRef}
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onTouchEnd={handleTouchEnd(confirmPasswordRef)}
                required
                disabled={loading}
                autoComplete="new-password"
                minLength={6}
                style={{
                  ...nativeInputStyle,
                  backgroundColor: loading ? '#f5f5f5' : 'white',
                }}
              />
            </div>

            <Button 
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? t('resetPassword.resetting') : t('resetPassword.resetButton')}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                component={Link}
                to="/login"
                color="primary"
                startIcon={<ArrowBack />}
              >
                {t('forgotPassword.backToLogin')}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}
